import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Search, Compass, Plus, X, Loader2, LayoutDashboard } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { API, useAuth } from "@/App";
import { formatEventDate } from "@/lib/dates";
import CreateEventPicker from "@/components/CreateEventPicker";
import BrandMark from "@/components/BrandMark";

const getInitials = (name) => {
  if (!name) return "?";
  return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
};

const getCurrencySymbol = (c) => c === "GBP" ? "£" : c === "EUR" ? "€" : "$";

export const TopNav = ({ onExploreClick, onHostClick }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState({ games: [], users: [] });
  const [searching, setSearching] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Focus input when search opens
  useEffect(() => {
    if (searchOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setSearchQuery("");
      setSearchResults({ games: [], users: [] });
    }
  }, [searchOpen]);

  // Debounced search
  useEffect(() => {
    if (!searchQuery || searchQuery.trim().length < 2) {
      setSearchResults({ games: [], users: [] });
      return;
    }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`${API}/search?q=${encodeURIComponent(searchQuery.trim())}`);
        if (r.ok) {
          const data = await r.json();
          setSearchResults(data);
        }
      } catch (e) {
        // silent
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Close on outside click
  useEffect(() => {
    if (!searchOpen) return;
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [searchOpen]);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setSearchOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const handleExplore = () => {
    if (onExploreClick) {
      onExploreClick();
    } else {
      navigate("/");
      setTimeout(() => {
        const el = document.getElementById("games-grid");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  };

  const handleHost = () => {
    if (onHostClick) {
      onHostClick();
    } else if (!user) {
      navigate("/login", { state: { from: "/profile" } });
    } else {
      setPickerOpen(true);
    }
  };

  const handleHostHome = () => {
    if (!user) {
      navigate("/login", { state: { from: "/host" } });
    } else {
      navigate("/host");
    }
  };

  const goToGame = (id) => {
    setSearchOpen(false);
    navigate(`/game/${id}`);
  };
  const goToProfile = (uid) => {
    setSearchOpen(false);
    navigate(`/profile/${uid}`);
  };

  return (
    <header className="sticky top-0 z-50 bg-background border-b border-border safe-top" data-testid="top-nav">
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          {/* Left: Logo */}
          <button
            type="button"
            onClick={() => navigate("/")}
            className="flex items-center gap-2 shrink-0 focus:outline-none"
            data-testid="nav-logo"
            aria-label="Pouls home"
          >
            <div className="sm:hidden">
              <BrandMark size="md" showWordmark={false} />
            </div>
            <div className="hidden sm:block">
              <BrandMark size="md" />
            </div>
          </button>

          {/* Center: Capsule with Search | Explore | Host (compact, centered) */}
          <div className="flex-1 flex justify-center">
            <div ref={containerRef} className="relative">
              <div className="inline-flex items-stretch bg-muted/60 border border-border rounded-full p-1 gap-1">
                {/* Search — circular icon, hugs the left stadium end */}
                <button
                  type="button"
                  onClick={() => setSearchOpen(true)}
                  className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-muted/80 transition-colors shrink-0"
                  aria-label="Search"
                  data-testid="nav-search-btn"
                >
                  <Search className="w-4 h-4 text-muted-foreground" />
                </button>

                {/* Explore — active item: subtle highlight + accent bar at bottom */}
                <button
                  type="button"
                  onClick={handleExplore}
                  className={`relative flex items-center gap-1.5 px-4 sm:px-5 h-10 rounded-full hover:bg-muted/80 text-sm font-medium transition-colors ${location.pathname === "/" ? "bg-muted" : ""}`}
                  data-testid="nav-explore-btn"
                >
                  <Compass className="w-4 h-4" />
                  <span className="hidden sm:inline">Explore</span>
                  {location.pathname === "/" && (
                    <span className="absolute left-1/2 -translate-x-1/2 bottom-0.5 w-6 h-0.5 rounded-full bg-primary" aria-hidden />
                  )}
                </button>

                {/* Create */}
                <button
                  type="button"
                  onClick={handleHost}
                  className="flex items-center gap-1.5 px-4 sm:px-5 h-10 rounded-full hover:bg-muted/60 text-sm font-medium transition-colors"
                  data-testid="nav-host-btn"
                  aria-label="Create event"
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">Create</span>
                </button>
              </div>

            {/* Search dropdown */}
            {searchOpen && (
              <div
                className="absolute left-0 right-0 sm:left-1/2 sm:-translate-x-1/2 sm:w-[28rem] top-14 bg-card border border-border rounded-xl shadow-2xl overflow-hidden"
                data-testid="nav-search-dropdown"
              >
                <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
                  <Search className="w-4 h-4 text-muted-foreground" />
                  <input
                    ref={inputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search games, places or people..."
                    className="flex-1 bg-transparent border-0 outline-none text-sm placeholder:text-muted-foreground"
                    data-testid="nav-search-input"
                  />
                  {searching && <Loader2 className="w-4 h-4 text-muted-foreground animate-spin" />}
                  <button
                    type="button"
                    onClick={() => setSearchOpen(false)}
                    className="p-1 text-muted-foreground hover:text-foreground"
                    aria-label="Close search"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div className="max-h-96 overflow-y-auto">
                  {searchQuery.trim().length < 2 && (
                    <p className="text-sm text-muted-foreground px-4 py-6 text-center">
                      Type at least 2 characters to search
                    </p>
                  )}
                  {searchQuery.trim().length >= 2 && !searching && searchResults.games.length === 0 && searchResults.users.length === 0 && (
                    <p className="text-sm text-muted-foreground px-4 py-6 text-center">
                      Nothing found for "{searchQuery}"
                    </p>
                  )}

                  {searchResults.games.length > 0 && (
                    <div className="py-2">
                      <p className="px-4 py-1 text-xs text-muted-foreground uppercase tracking-wider font-display">Games</p>
                      {searchResults.games.map(g => (
                        <button
                          key={g.game_id}
                          type="button"
                          onClick={() => goToGame(g.game_id)}
                          className="w-full flex items-center gap-3 px-4 py-2 hover:bg-muted text-left"
                          data-testid={`search-game-${g.game_id}`}
                        >
                          <img src={g.image_url} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{g.title}</p>
                            <p className="text-xs text-muted-foreground truncate">
                              {g.city} · {formatEventDate(g.date, { day: "numeric", month: "short" })}
                              {g.price > 0 && ` · ${getCurrencySymbol(g.currency)}${g.price}`}
                              {g.price === 0 && " · Free"}
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {searchResults.users.length > 0 && (
                    <div className="py-2 border-t border-border">
                      <p className="px-4 py-1 text-xs text-muted-foreground uppercase tracking-wider font-display">People</p>
                      {searchResults.users.map(u => (
                        <button
                          key={u.user_id}
                          type="button"
                          onClick={() => goToProfile(u.user_id)}
                          className="w-full flex items-center gap-3 px-4 py-2 hover:bg-muted text-left"
                          data-testid={`search-user-${u.user_id}`}
                        >
                          <Avatar className="w-10 h-10 shrink-0">
                            <AvatarImage src={u.picture} alt={u.name} />
                            <AvatarFallback className="bg-primary/20 text-primary text-sm">{getInitials(u.name)}</AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{u.name || "Player"}</p>
                            {u.username && <p className="text-xs text-muted-foreground truncate">@{u.username}</p>}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
            </div>
          </div>

          {/* Right: Host button + Avatar (or Login) */}
          <div className="shrink-0 flex items-center gap-2 sm:gap-3">
            <button
              type="button"
              onClick={handleHostHome}
              className={`relative flex items-center gap-1.5 h-9 px-3 sm:px-4 rounded-full text-sm font-semibold transition-colors shadow-sm ${
                location.pathname.startsWith("/host")
                  ? "bg-foreground text-background hover:bg-foreground/90"
                  : "bg-primary text-primary-foreground hover:bg-primary/90"
              }`}
              data-testid="nav-host-home-btn"
              aria-label="Host dashboard"
            >
              <LayoutDashboard className="w-4 h-4" />
              <span className="hidden sm:inline">Host Hub</span>
            </button>
            {user ? (
              <button
                type="button"
                onClick={() => navigate("/profile")}
                className="rounded-full focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid="nav-avatar-btn"
                aria-label="My profile"
              >
                <Avatar className="w-9 h-9 ring-1 ring-border">
                  <AvatarImage src={user.picture} alt={user.name} />
                  <AvatarFallback className="bg-primary/20 text-primary text-xs">{getInitials(user.name)}</AvatarFallback>
                </Avatar>
              </button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/login")}
                className="border-primary/30 hover:bg-primary/10"
                data-testid="nav-login-btn"
              >
                Login
              </Button>
            )}
          </div>
        </div>
      </div>
      <CreateEventPicker open={pickerOpen} onOpenChange={setPickerOpen} />
    </header>
  );
};

export default TopNav;
